import Image from 'next/image'
import React, { useMemo, useState } from 'react'
import { useTheme } from 'styled-components'
import { StickySection } from '../../styles/ProductDetail.style'

import { AdBanner } from '@/components/Banner/AdBanner/AdBanner'
import { useBooks } from '@/components/Books/Order'
import { ExtraResourceNew } from '@/components/Product/ExtraResourceNew'
import Icon from '@/components/icon/Icon'
import { ArrowRight, Sizes } from '@/components/icon/IconList'
import { mixpanelEvent } from '@/lib/mixpanels/mixpanel'
import useWindowDimensions from '@/lib/web-ui/useWindowDimensions'
import { useQuery } from '@tanstack/react-query'
import Link from 'next/link'
import { Link as ScrollLink } from 'react-scroll'
import { BooksProduct, isBooksProduct } from 'src/api/booksProducts'
import { getLabByAuthorId } from 'src/api/lab'
import { HandoutDTO, IProduct, TextbookDTO } from 'src/api/product'
import { getPublisher } from 'src/api/publisher'
import {
	AuthorTab,
	BookInfoTab,
	ContentsTab,
	DetailTab,
	PreviewTab,
	PriceTab,
	ProductDetailContentWrapper,
	ScrollSection,
	StyledAnchor,
} from './ProductDetailContent.style'
import ProductDetailTerms from './ProductDetailTerms'
import detailInfoList from './detailInfoList'

export interface ProductDetailContentProps {
	isDeleted?: boolean
	product: IProduct | BooksProduct
	priceSection: React.ReactNode // TODO: remove this
}

const enum LinkTab {
	contents = 'contents',
	author = 'author',
	preview = 'preview',
	detail = 'detail',
	refund = 'refund',
	book = 'book',
	bookShipRefund = 'bookShipRefund',
	review = 'review',
}

export const ProductDetailContent: React.FC<ProductDetailContentProps> = ({
	product,
	priceSection,
}) => {
	const isBooks = isBooksProduct(product)
	const { isBooks: isAladinbook } = useBooks()
	const isEbook = isBooks && !isAladinbook
	const productType = isBooks ? product.productType : product.source_type

	// TODO: remove this
	const { data: publisherRes } = useQuery(
		['productDetail', product.id, 'publisher'],
		// TODO: remove type assertion
		() =>
			isBooks
				? null
				: getPublisher({ id: String((product.source as TextbookDTO).publisher_id) }),
		{
			enabled: productType === 'textbook' || productType === 'workbook' || isBooks,
		},
	)
	const publisher = publisherRes?.publisher.name

	// TODO: remove this
	//연구소
	const { data: authorLabRes } = useQuery(
		['productDetail', product.id, 'authorLab'],
		() => (isBooks ? null : getLabByAuthorId(product.author_id)),
		{
			enabled: productType === 'handout',
			retry: 0,
		},
	)
	const authorLab = authorLabRes?.lab

	const theme = useTheme()
	const source = isBooks ? product : product.source
	const { sub_items } = source as TextbookDTO
	const { images } = source as HandoutDTO
	const previewImages = images?.previews
	const promotionImages =
		product && images?.promotions
			? Array.isArray(images?.promotions) &&
			  images?.promotions.slice().sort((a, b) => a.order - b.order)
			: []
	const hasSubItems = useMemo(() => {
		return sub_items && sub_items.length > 0
	}, [sub_items])
	const { width } = useWindowDimensions()
	const isMobile = width < 1200
	const [scrollLinkOptions] = useState({
		activeClass: 'active',
		smooth: true,
		offset: isMobile ? -200 : -150,
		spy: true,
	})

	const TabList = [
		{
			key: LinkTab.book,
			title: '도서 정보',
			render: isBooks && (product.introduction || product.tableOfContents),
		},
		{
			key: LinkTab.contents,
			title: '포함된 컨텐츠',
			render: !isBooks && hasSubItems,
		},
		{
			key: LinkTab.author,
			title: '저자 정보',
			render: !isBooks && authorLab,
		},
		{
			key: LinkTab.preview,
			title: '미리보기',
			render: !isBooks && previewImages,
		},
		{
			key: LinkTab.detail,
			title: '상세 정보',
			render: true,
		},
		{
			key: LinkTab.refund,
			title: '환불/교환 정보',
			render: !isBooks || isEbook,
		},

		{
			key: LinkTab.bookShipRefund,
			title: '배송/환불/교환 정보',
			render: isAladinbook,
		},
	]

	return (
		<ProductDetailContentWrapper>
			<ScrollSection>
				<StyledAnchor>
					{TabList.map((tab, index) => {
						return tab.render ? (
							<li key={`bottom-tab-${index}`}>
								<ScrollLink
									to={tab.key}
									{...scrollLinkOptions}
									onClick={() =>
										mixpanelEvent('Product Detail Page Tab Clicked', {
											'Tab Name': tab.title,
										})
									}>
									{tab.title}
								</ScrollLink>
							</li>
						) : null
					})}
				</StyledAnchor>
				<AdBanner
					position={'productDetail'}
					mixpanelPosition={'product detail page'}
					subject={isBooks ? product.subject : product.source.subject}
				/>
				<div className={'detail-content'}>
					{/*이북 홍보이미지*/}
					{isEbook && (
						<div>
							<img
								src={`/static/assets/${
									isMobile ? 'Mobile' : 'PC'
								}-image-goodnote.jpg`}
								alt={'ebook-promotion-img'}
							/>
						</div>
					)}
					{hasSubItems && (
						<ContentsTab name="contents">
							<h3>포함된 컨텐츠</h3>
							<div>
								{sub_items.map((subItem, index) => {
									return (
										<div key={`sub-item-${index}`}>
											<ExtraResourceNew type={subItem.type} />
											<span>{subItem.title}</span>
										</div>
									)
								})}
							</div>
						</ContentsTab>
					)}
					{authorLab && (
						<AuthorTab name={'author'}>
							<p>Created By</p>
							<h5>
								<div
									style={{
										backgroundColor: `${authorLab.custom_meta.color1}`,
									}}
									className={'img-wrapper'}>
									<Image
										src={authorLab.images.top_banner}
										alt={'연구소 이미지'}
										width={32}
										height={32}
									/>
								</div>
								{authorLab.author}
								<Link
									href={'/@' + authorLab.lab_name}
									onClick={() =>
										mixpanelEvent('Product Detail Page Lab Button Clicked', {
											'Lab Button Proposition': 'Info section',
										})
									}>
									연구소 바로가기
									<Icon
										icon={ArrowRight}
										size={Sizes.xsmall}
										color={theme.new.colors.sol_indigo_500}
									/>
								</Link>
							</h5>
							<div>{authorLab.content.replaceAll('^', '')}</div>
						</AuthorTab>
					)}
					{previewImages && (
						<PreviewTab name={'preview'}>
							<h3>미리보기</h3>
							<div>
								{previewImages
									.sort((a, b) => a.page_num - b.page_num)
									.map((image, index) => {
										return (
											<Image
												key={'preview-image-' + index}
												alt={`${product.title}_preview_${image.page_num}`}
												width={3000}
												height={6000}
												src={image.url}
											/>
										)
									})}
								{promotionImages &&
									promotionImages.map((image, index) => {
										return (
											<Image
												key={`detail-image-${index}`}
												width={3000}
												height={6000}
												src={image.url}
												alt={'product-detail-image'}
											/>
										)
									})}
							</div>
						</PreviewTab>
					)}
					{isBooks && (product.introduction || product.tableOfContents) && (
						<BookInfoTab name={'book'}>
							<h3>도서 정보</h3>
							{product.introduction && (
								<div className={'info-cell'}>
									<p className={'title'}>책소개</p>
									<p
										className={'content'}
										dangerouslySetInnerHTML={{ __html: product.introduction }}
									/>
								</div>
							)}
							{product.tableOfContents && (
								<div className={'info-cell'}>
									<p className={'title'}>목차</p>
									<p
										className={'content'}
										dangerouslySetInnerHTML={{
											__html: product.tableOfContents,
										}}
									/>
								</div>
							)}
						</BookInfoTab>
					)}
					<DetailTab name={'detail'}>
						<h3>상세 정보</h3>
						{product?.description && (
							<div className={'wrapper description'}>{product?.description}</div>
						)}
						<div className={'wrapper info-wrapper'}>
							<div>
								<ul>
									{detailInfoList(
										product,
										isBooks ? product.publisher : publisher,
									).map((item, index) => {
										if (
											item.value === null ||
											item.value === undefined ||
											item.value === '0' ||
											item.value === ''
										)
											return null
										return (
											<li key={`detail-info-${index}`}>
												<span className={'title'}>{item.type}</span>
												<span className={'content'}>{item.value}</span>
											</li>
										)
									})}
								</ul>
							</div>
						</div>
					</DetailTab>
					<ProductDetailTerms productType={productType as BooksProduct['productType']} />
				</div>
			</ScrollSection>
			<StickySection>
				<PriceTab>{priceSection}</PriceTab>
			</StickySection>
		</ProductDetailContentWrapper>
	)
}
